/**
 * Pour functionality canonical codes.
 * Stored in PourInfo.Functionality. UI must always render via
 * `functionalityLabel(...)` instead of showing the raw code.
 */
export type FunctionalityCode = '1' | '2' | '3';

const LABELS: Record<FunctionalityCode, string> = {
  '1': 'Concrete Maturity',
  '2': 'Mass Concrete',
  '3': 'Maturity & Mass Concrete',
};

// Short display names for the granular 6-option monitoring functions
// (PourInfo.MonitoringFunctions canonical strings). "Temperature Only" keeps
// the Mass Concrete wording — its selector title is "TEMPERATURE ONLY (Mass
// Concrete Requirements)".
const FUNCTION_SHORT_NAMES: Record<string, string> = {
  'Concrete Maturity': 'Maturity',
  'Temperature Only': 'Mass Concrete',
  'Temperature Match Curing': 'Match Curing',
  'External Temperature and Humidity': 'Temp & Humidity',
  'Onsite Weather Conditions': 'Weather',
  'Calculate Concrete Evaporation Rate': 'Evaporation',
};

/**
 * Map PourInfo.Functionality (`'1'` | `'2'` | `'3'`) to a human-readable label.
 * When the pour's granular MonitoringFunctions are known, they win — a
 * Temp & Humidity pour derives legacy code '2' and would otherwise mislabel
 * as "Mass Concrete". Returns `'-'` for missing/empty values. Unknown codes
 * fall through unchanged (defensive).
 */
export function functionalityLabel(
  value?: string | null,
  monitoringFunctions?: string | null,
): string {
  const fns = (monitoringFunctions ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  if (fns.length > 0) {
    return fns.map((f) => FUNCTION_SHORT_NAMES[f] || f).join(' + ');
  }
  if (value === null || value === undefined) return '-';
  const trimmed = String(value).trim();
  if (trimmed === '') return '-';
  if (trimmed === '1' || trimmed === '2' || trimmed === '3') {
    return LABELS[trimmed as FunctionalityCode];
  }
  return trimmed;
}

/** What a pour monitors, for the phone layout's per-function choices. */
export interface PourFunctionFlags {
  /** The pour records its granular MonitoringFunctions; false for legacy
   *  pours, whose flags come from the coarse Functionality code. */
  hasFunctions: boolean;
  /** Maturity / strength is computed (the dashboard's Strength section). */
  maturity: boolean;
  /** A concrete temperature function is present (false for a pour with
   *  only ambient or weather functions). Says nothing about mass concrete:
   *  PourCreate adds "Temperature Only" to every maturity, match-curing and
   *  evaporation pour. */
  temperature: boolean;
  evaporation: boolean;
  /** External Temperature and Humidity (ConXenviro ambient hardware). */
  ambient: boolean;
  /** Weather data belongs to the pour (the dashboard's Weather section):
   *  Onsite Weather Conditions or evaporation. External Temperature and
   *  Humidity is ambient-sensor data, not weather. */
  weather: boolean;
  /** A legacy pour (no functions recorded) whose code is '2' Mass Concrete
   *  or '3' Maturity & Mass Concrete. Pours with functions derive their
   *  code from them, so the code is not read for those. */
  legacyMassConcrete: boolean;
}

const CONCRETE_FUNCTIONS = [
  'Concrete Maturity',
  'Temperature Only',
  'Temperature Match Curing',
  'Calculate Concrete Evaporation Rate',
];

/**
 * A pour's function flags from PourInfo.MonitoringFunctions (comma-joined)
 * with the legacy Functionality code as the fallback when none are
 * recorded. The same parsing and fallbacks as the desktop dashboard's
 * section gating (PourDashboardPage visibleNavItems) and LocationDetails'
 * graph gating, so phone and desktop offer the same sections. Works for a
 * Control Panel row (MonitoringFunctions, Functionality) and the dashboard
 * pour summary (monitoring_functions, functionality) alike.
 */
export function pourFunctionFlags(
  monitoringFunctions?: string | null,
  functionality?: string | null,
): PourFunctionFlags {
  const fns = (monitoringFunctions ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  const hasFunctions = fns.length > 0;
  const code = String(functionality ?? '').trim();
  const evaporation = hasFunctions && fns.includes('Calculate Concrete Evaporation Rate');
  if (hasFunctions) {
    return {
      hasFunctions,
      maturity: fns.includes('Concrete Maturity'),
      temperature: fns.some((f) => CONCRETE_FUNCTIONS.includes(f)),
      evaporation,
      ambient: fns.includes('External Temperature and Humidity'),
      weather: fns.includes('Onsite Weather Conditions') || evaporation,
      legacyMassConcrete: false,
    };
  }
  const legacyMassConcrete = code === '2' || code === '3';
  return {
    hasFunctions,
    maturity: code === '1' || code === '3',
    // Legacy pours are concrete pours.
    temperature: true,
    evaporation: false,
    ambient: false,
    weather: legacyMassConcrete,
    legacyMassConcrete,
  };
}

/**
 * Whether a pour is treated as mass concrete (differential tile, worst
 * differential in the status strip). Detected from data: configured
 * differential pairs (the dashboard's differentials, the list's
 * MaxDifferentialNow / MaxDifferentialLimit, a location's
 * temp_diff_location_ids), or the legacy code '2' / '3'. Never from the
 * "Temperature Only" function, which PourCreate adds to every maturity,
 * match-curing and evaporation pour, so a "Concrete Maturity, Temperature
 * Only" pour is not mass concrete without differential pairs.
 */
export function isMassConcretePour({
  flags,
  hasDifferentialPairs,
}: {
  flags: PourFunctionFlags;
  hasDifferentialPairs: boolean;
}): boolean {
  return flags.legacyMassConcrete || hasDifferentialPairs;
}
